import React, { useEffect } from "react";
import "./App.css";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import { AdminRoutes, CoachRoutes, StudentRoutes } from "./config/routes";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import NoMatch from "./pages/NoMatch";
import NoData from "./pages/NoData";
import SetPassword from "./pages/SetPassword";
import Notification from "./utils/Notification";
import ResetPasswordRequest from "./pages/ResetPasswordRequest";
import Unauthorized from "./pages/Unauthorized";
import ProtectedRoute from "./utils/ProtectedRoute";
import { ROLES } from "./types/user";
import { ApolloProvider } from "@apollo/client";
import { checkLoggedUserThunk } from "./reduxStore/auth/thunks";
import { rememberFeedbackReturnPath } from "./services/accountSession";
import { useAppDispatch } from "./hooks/useAppDispatch";
import OrganizationsList from "./pages/Organization/list";
import TagList from "./pages/Tag/list";
import { BadgeList } from "./pages/Badge/list";
import CampaignList from "./pages/Campaign/list";
import { CampaignAnswer } from "./pages/Campaign/answer";
import { CampaignCreateOrEdit } from "./pages/Campaign/CreateOrEdit";
import ResourcesList from "./pages/Resource/list";
import client from "./utils/ApolloClient";
import { ProtectedLayout } from "./components/Layout";
import UserList from "./pages/User/list";
import { EPAList, CompetencyList, ITERList } from "./pages/Requirements/list";
import {
  EPACreateAndEdit,
  CompetencyCreateAndEdit,
  ITERCreateAndEdit,
} from "./pages/Requirements/CreateAndEdit";
import { SurveyList } from "./pages/Survey/list";
import { SurveyCreateAndEdit } from "./pages/Survey/CreateAndEdit";
import { SurveyDetail } from "./pages/Survey/detail";
import { SurveyAnswer } from "./pages/Survey/answer";
import { AnswersList, SurveyAnswers } from "./pages/Answer/list";
import { AnswerDetail } from "./pages/Answer/detail";
import { Home } from "./pages/Home";
import { SpeciesList } from "./pages/Species/list";
import { DomainList } from "./pages/Domain/list";
import { RequirementPortal } from "./pages/Requirements/portal";
import { AcademicYearList } from "./pages/AcademicYear/list";
import { AcademicCreateOrEdit } from "./pages/AcademicYear/CreateOrEdit";
import {
  FeedbackList,
  FeedbackPortalList,
  OrganizationUserFeedbackList,
} from "pages/Feedback/List";
import { FeedbackGuide } from "pages/Feedback/FeedbackGuide";
import { PublicLayout } from "components/Layout/PublicLayout";
import { AcademicYearCreateByOrgProgram } from "pages/AcademicYear/AcademicYearCreateByOrgProgram";
import { GraduationClassList } from "pages/GraduationClass/list";
import { GraduationClassCreateOrEdit } from "pages/GraduationClass/CreateOrEdit";
import { RequestFeedback } from "pages/Feedback/RequestFeedback";
import { WellnessResourceList } from "./pages/WellnessResource/list";
import { WellnessResourceView } from "./pages/WellnessResource/view";
import { UnderConstruction } from "pages/UnderConstruction";
import {
  CompetencyDetail,
  EPADetail,
  ITERDetail,
} from "pages/Requirements/detail";
import { GraduationClassDetail } from "pages/GraduationClass/detail";
import { AcademicYearDetail } from "pages/AcademicYear/detail";
import { SitesList } from "pages/Sites/list";
import { SiteCreateOrEdit } from "pages/Sites/CreateOrEdit";
import { CreateOrEditOrganization } from "pages/Organization/createOrEdit";
import { UploadCSVUsers } from "pages/UploadCSV/UploadCSVUsers";
import { UploadCSVPrograms } from "pages/UploadCSV/UploadCSVPrograms";
import { UploadCSVCohorts } from "pages/UploadCSV/UploadCSVCohorts/index";
import { UploadCSVRequirements } from "pages/UploadCSV/UploadCSVRequirements";
import { ImportFrameworkSpreadsheet } from "pages/UploadSpreadsheetFramework";
import { FeedbackPortalDetail } from "pages/Feedback/Detail";
import { ResourceDetail } from "pages/Resource/detail";
import { OrganizationDetail } from "pages/Organization/detail";
import { YearlyCurriculumList } from "pages/YearlyCurriculum/List";
import { Profile } from "pages/Profile";
import { UserDetail } from "pages/User/detail";
import { UserEdit } from "pages/User/edit";
import { Analytics } from "pages/Analytics";
import { Metrics } from "pages/Metrics";
import { HospitalCompetition } from "pages/HospitalCompetition";
import { EducationRecordAccessLog } from "pages/EducationRecordAccessLog";
import { Onboarding } from "pages/Onboarding";
import { AndroidAppPrivacyPolicy } from "pages/Privacy";
import TermsOfUse from "./pages/TermsOfUse";
import { OnboardingStudent } from "pages/OnboardingStudent";
import { CustomUserList } from "pages/User/customList";
import { CustomUserDetail } from "pages/User/customDetail";
import { MentorList } from "pages/User/mentorList";
import LoginCode from "./pages/LoginCode";
import MfaChallenge from "./pages/Mfa";
import AuthCallback from "./pages/AuthCallback";
import AccountRecovery from "./pages/AccountRecovery";
import {
  FindAccountGuide,
  LoginCodeGuide,
  ResetPasswordGuide,
} from "./pages/AuthGuides";
import { Chat } from "pages/Chat";
import { InAppNotifications } from "pages/Notifications";
import { PostCreate, PostDetail, PostList } from "pages/Post";
import { VetStore } from "pages/VetStore";
import { FeedbackSubmissionConfirmation } from "pages/Feedback/SubmissionConfirmation";
import { ErrorBoundary } from "components/ErrorBoundary";

const App: React.FC = () => {
  const dispatch = useAppDispatch();
  rememberFeedbackReturnPath();

  useEffect(() => {
    dispatch(checkLoggedUserThunk());
  }, [dispatch]);

  return (
    <ErrorBoundary>
      <ApolloProvider client={client}>
        <Router>
          <Routes>
            <Route
              path={AdminRoutes.Onboarding}
              element={
                <ProtectedRoute
                  element={<Onboarding />}
                  roles={[ROLES.ADMIN, ROLES.DIRECTOR, ROLES.MANAGER]}
                />
              }
            />
            <Route
              path={StudentRoutes.OrganizationOnboardingStudentList}
              element={
                <ProtectedRoute
                  element={<OnboardingStudent />}
                  roles={[ROLES.ADMIN, ROLES.STUDENT]}
                />
              }
            />
            <Route element={<PublicLayout />}>
              <Route
                path={AdminRoutes.ResetPassword}
                element={<ResetPassword />}
              />
              <Route
                path={AdminRoutes.ResetPasswordRequest}
                element={<ResetPasswordRequest />}
              />
              <Route
                path={AdminRoutes.AccountRecovery}
                element={<AccountRecovery />}
              />
              <Route
                path={AdminRoutes.FindAccountGuide}
                element={<FindAccountGuide />}
              />
              <Route
                path={AdminRoutes.ResetPasswordGuide}
                element={<ResetPasswordGuide />}
              />
              <Route
                path={AdminRoutes.LoginCodeGuide}
                element={<LoginCodeGuide />}
              />
              <Route path={AdminRoutes.SetPassword} element={<SetPassword />} />
              <Route path={AdminRoutes.Mfa} element={<MfaChallenge />} />
              <Route path={AdminRoutes.LoginCode} element={<LoginCode />} />
              <Route
                path={AdminRoutes.AuthCallback}
                element={<AuthCallback />}
              />

              <Route
                path={AdminRoutes.AndroidPrivacy}
                element={<AndroidAppPrivacyPolicy />}
              />
              <Route path="*" element={<Login />} />
            </Route>

            <Route element={<ProtectedLayout />}>
              <Route
                path={AdminRoutes.Home}
                element={
                  <ProtectedRoute
                    element={<Home />}
                    roles={[
                      ROLES.COACH,
                      ROLES.STUDENT,
                      ROLES.ADMIN,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                    ]}
                  />
                }
              />
              <Route
                path={AdminRoutes.Notifications}
                element={
                  <ProtectedRoute
                    element={<InAppNotifications />}
                    roles={[
                      ROLES.ADMIN,
                      ROLES.COACH,
                      ROLES.STUDENT,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                    ]}
                  />
                }
              />
              <Route
                path={AdminRoutes.Chat}
                element={
                  <ProtectedRoute
                    element={<Chat />}
                    roles={[
                      ROLES.ADMIN,
                      ROLES.COACH,
                      ROLES.STUDENT,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                    ]}
                  />
                }
              />
              <Route
                path={AdminRoutes.Posts}
                element={
                  <ProtectedRoute
                    element={<PostList />}
                    roles={[
                      ROLES.ADMIN,
                      ROLES.COACH,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                      ROLES.STUDENT,
                    ]}
                  />
                }
              />
              <Route
                path={AdminRoutes.PostCreate}
                element={
                  <ProtectedRoute
                    element={<PostCreate />}
                    roles={[
                      ROLES.ADMIN,
                      ROLES.COACH,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                      ROLES.STUDENT,
                    ]}
                  />
                }
              />
              <Route
                path={AdminRoutes.PostDetail}
                element={
                  <ProtectedRoute
                    element={<PostDetail />}
                    roles={[
                      ROLES.ADMIN,
                      ROLES.COACH,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                      ROLES.STUDENT,
                    ]}
                  />
                }
              />
              <Route
                path={AdminRoutes.Profile}
                element={
                  <ProtectedRoute
                    element={<Profile />}
                    roles={[
                      ROLES.ADMIN,
                      ROLES.COACH,
                      ROLES.STUDENT,
                      ROLES.DIRECTOR,
                      ROLES.MANAGER,
                    ]}
                  />
                }
              />
              <Route
                path={AdminRoutes.UploadCSVUsers}
                element={
                  <ProtectedRoute
                    element={
                      <UploadCSVUsers
                        roles={[
                          ROLES.MANAGER,
                          ROLES.DIRECTOR,
                          ROLES.STUDENT,
                          ROLES.COACH,
                        ]}
                      />
                    }
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.UploadCSVPrograms}
                element={
                  <ProtectedRoute
                    element={<UploadCSVPrograms />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.UploadCSVCohorts}
                element={
                  <ProtectedRoute
                    element={<UploadCSVCohorts />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.UploadCSVRequirementsEPA}
                element={
                  <ProtectedRoute
                    element={
                      <UploadCSVRequirements
                        mode="global"
                        requirementType="EPA"
                      />
                    }
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.UploadCSVRequirementsCompetency}
                element={
                  <ProtectedRoute
                    element={
                      <UploadCSVRequirements
                        mode="global"
                        requirementType="COMPETENCY"
                      />
                    }
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.UploadCSVRequirementsITER}
                element={
                  <ProtectedRoute
                    element={
                      <UploadCSVRequirements
                        mode="global"
                        requirementType="ITER"
                      />
                    }
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.UploadSpreadsheetFramework}
                element={
                  <ProtectedRoute
                    element={<ImportFrameworkSpreadsheet mode="global" />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.User}
                element={
                  <ProtectedRoute
                    element={<UserList />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.UserDetail}
                element={
                  <ProtectedRoute
                    element={<UserDetail />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.UserEdit}
                element={
                  <ProtectedRoute
                    element={<UserEdit />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />

              <Route
                path={AdminRoutes.OrganizationLearners}
                element={
                  <ProtectedRoute
                    element={<CustomUserList role={ROLES.STUDENT} />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationLearnerDetail}
                element={
                  <ProtectedRoute
                    element={<CustomUserDetail role={ROLES.STUDENT} />}
                    roles={[
                      ROLES.ADMIN,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                      ROLES.COACH,
                    ]}
                  />
                }
              />
              <Route
                path={CoachRoutes.LearnerDetail}
                element={
                  <ProtectedRoute
                    element={<CustomUserDetail role={ROLES.STUDENT} />}
                    roles={[ROLES.COACH]}
                  />
                }
              />
              <Route
                path={CoachRoutes.Learners}
                element={
                  <ProtectedRoute
                    element={<CustomUserList role={ROLES.STUDENT} />}
                    roles={[ROLES.COACH]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationMentors}
                element={
                  <ProtectedRoute
                    element={<CustomUserList role={ROLES.COACH} />}
                    roles={[
                      ROLES.ADMIN,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                      ROLES.STUDENT,
                    ]}
                  />
                }
              />
              <Route
                path={StudentRoutes.MentorList}
                element={
                  <ProtectedRoute
                    element={<MentorList />}
                    roles={[ROLES.STUDENT]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationMentorDetail}
                element={
                  <ProtectedRoute
                    element={<CustomUserDetail role={ROLES.COACH} />}
                    roles={[
                      ROLES.ADMIN,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                      ROLES.STUDENT,
                    ]}
                  />
                }
              />

              <Route
                path={AdminRoutes.OrganizationList}
                element={
                  <ProtectedRoute
                    element={<OrganizationsList />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />

              <Route
                path={AdminRoutes.OrganizationCreate}
                element={
                  <ProtectedRoute
                    element={<CreateOrEditOrganization />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationEdit}
                element={
                  <ProtectedRoute
                    element={<CreateOrEditOrganization />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.TagsList}
                element={
                  <ProtectedRoute element={<TagList />} roles={[ROLES.ADMIN]} />
                }
              />
              <Route
                path={AdminRoutes.BadgesList}
                element={
                  <ProtectedRoute
                    element={<BadgeList />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.VetStore}
                element={
                  <ProtectedRoute
                    element={<VetStore />}
                    roles={[
                      ROLES.ADMIN,
                      ROLES.COACH,
                      ROLES.STUDENT,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                    ]}
                  />
                }
              />
              <Route
                path={AdminRoutes.CampaignsList}
                element={
                  <ProtectedRoute
                    element={<CampaignList />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationCampaignsList}
                element={
                  <ProtectedRoute
                    element={<CampaignList />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationCampaignCreate}
                element={
                  <ProtectedRoute
                    element={<CampaignCreateOrEdit />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationCampaignEdit}
                element={
                  <ProtectedRoute
                    element={<CampaignCreateOrEdit edit />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.CampaignCreate}
                element={
                  <ProtectedRoute
                    element={<CampaignCreateOrEdit />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.CampaignEdit}
                element={
                  <ProtectedRoute
                    element={<CampaignCreateOrEdit edit />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.CampaignAnswer}
                element={
                  <ProtectedRoute
                    element={<CampaignAnswer />}
                    roles={[ROLES.ADMIN, ROLES.COACH, ROLES.STUDENT]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationDetail}
                element={
                  <ProtectedRoute
                    element={<OrganizationDetail />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationDetailView}
                element={
                  <ProtectedRoute
                    element={<OrganizationDetail />}
                    roles={[
                      ROLES.ADMIN,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                      ROLES.COACH,
                      ROLES.STUDENT,
                    ]}
                  />
                }
              />
              <Route
                path={AdminRoutes.WellnessResourcesList}
                element={
                  <ProtectedRoute
                    element={<WellnessResourceList />}
                    roles={[
                      ROLES.ADMIN,
                      ROLES.DIRECTOR,
                      ROLES.MANAGER,
                      ROLES.COACH,
                      ROLES.STUDENT,
                    ]}
                  />
                }
              />
              <Route
                path={AdminRoutes.WellnessResourcesView}
                element={
                  <ProtectedRoute
                    element={<WellnessResourceView />}
                    roles={[
                      ROLES.ADMIN,
                      ROLES.COACH,
                      ROLES.STUDENT,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                    ]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationWellnessResourcesList}
                element={
                  <ProtectedRoute
                    element={<WellnessResourceList />}
                    roles={[
                      ROLES.ADMIN,
                      ROLES.COACH,
                      ROLES.STUDENT,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                    ]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationWellnessResourcesDetail}
                element={
                  <ProtectedRoute
                    element={<WellnessResourceView />}
                    roles={[
                      ROLES.ADMIN,
                      ROLES.COACH,
                      ROLES.STUDENT,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                    ]}
                  />
                }
              />
              <Route
                path={AdminRoutes.ResourcesList}
                element={
                  <ProtectedRoute
                    element={<ResourcesList />}
                    roles={[
                      ROLES.ADMIN,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                      ROLES.COACH,
                      ROLES.STUDENT,
                    ]}
                  />
                }
              />
              <Route
                path={AdminRoutes.ResourcesDetail}
                element={
                  <ProtectedRoute
                    element={<ResourceDetail />}
                    roles={[
                      ROLES.ADMIN,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                      ROLES.COACH,
                      ROLES.STUDENT,
                    ]}
                  />
                }
              />
              <Route
                path={AdminRoutes.EPAList}
                element={
                  <ProtectedRoute element={<EPAList />} roles={[ROLES.ADMIN]} />
                }
              />
              <Route
                path={AdminRoutes.EPACreate}
                element={
                  <ProtectedRoute
                    element={<EPACreateAndEdit />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.EPADetail}
                element={
                  <ProtectedRoute
                    element={<EPADetail />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.EPAEdit}
                element={
                  <ProtectedRoute
                    element={<EPACreateAndEdit edit />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.CompetenciesList}
                element={
                  <ProtectedRoute
                    element={<CompetencyList />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.CompetenciesCreate}
                element={
                  <ProtectedRoute
                    element={<CompetencyCreateAndEdit />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.CompetenciesDetail}
                element={
                  <ProtectedRoute
                    element={<CompetencyDetail />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.CompetenciesEdit}
                element={
                  <ProtectedRoute
                    element={<CompetencyCreateAndEdit edit />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.ITERsList}
                element={
                  <ProtectedRoute
                    element={<ITERList />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.ITERsCreate}
                element={
                  <ProtectedRoute
                    element={<ITERCreateAndEdit />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.ITERsDetail}
                element={
                  <ProtectedRoute
                    element={<ITERDetail />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.ITERsEdit}
                element={
                  <ProtectedRoute
                    element={<ITERCreateAndEdit edit />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.SurveysList}
                element={
                  <ProtectedRoute
                    element={<SurveyList />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.SurveyCreate}
                element={
                  <ProtectedRoute
                    element={<SurveyCreateAndEdit />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.SurveyEdit}
                element={
                  <ProtectedRoute
                    element={<SurveyCreateAndEdit edit />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path="/survey/:surveyId"
                element={
                  <ProtectedRoute
                    element={<SurveyDetail />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.SurveyAnswer}
                element={
                  <ProtectedRoute
                    element={<SurveyAnswer />}
                    roles={[
                      ROLES.ADMIN,
                      ROLES.STUDENT,
                      ROLES.COACH,
                      ROLES.DIRECTOR,
                      ROLES.MANAGER,
                    ]}
                  />
                }
              />
              <Route
                path={AdminRoutes.AnswersList}
                element={
                  <ProtectedRoute
                    element={<AnswersList />}
                    roles={[ROLES.ADMIN, ROLES.DIRECTOR, ROLES.MANAGER]}
                  />
                }
              />
              <Route
                path={AdminRoutes.SurveyAnswers}
                element={
                  <ProtectedRoute
                    element={<SurveyAnswers />}
                    roles={[ROLES.ADMIN, ROLES.DIRECTOR, ROLES.MANAGER]}
                  />
                }
              />
              <Route
                path={AdminRoutes.AnswerDetail}
                element={
                  <ProtectedRoute
                    element={<AnswerDetail />}
                    roles={[ROLES.ADMIN, ROLES.DIRECTOR, ROLES.MANAGER]}
                  />
                }
              />
              <Route
                path={AdminRoutes.SpeciesList}
                element={
                  <ProtectedRoute
                    element={<SpeciesList />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.DomainsList}
                element={
                  <ProtectedRoute
                    element={<DomainList />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.SitesList}
                element={
                  <ProtectedRoute
                    element={<SitesList />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.SiteCreate}
                element={
                  <ProtectedRoute
                    element={<SiteCreateOrEdit />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.SiteEdit}
                element={
                  <ProtectedRoute
                    element={<SiteCreateOrEdit />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.AcademicYearList}
                element={
                  <ProtectedRoute
                    element={<AcademicYearList />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.AcademicYearCreate}
                element={
                  <ProtectedRoute
                    element={<AcademicCreateOrEdit />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.AcademicYearEdit}
                element={
                  <ProtectedRoute
                    element={<AcademicCreateOrEdit edit />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.AcademicYearDetail}
                element={
                  <ProtectedRoute
                    element={<AcademicYearDetail />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />

              <Route
                path={StudentRoutes.Requirements}
                element={
                  <ProtectedRoute
                    element={<RequirementPortal />}
                    roles={[
                      ROLES.STUDENT,
                      ROLES.COACH,
                      ROLES.ADMIN,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                    ]}
                  />
                }
              />
              <Route
                path={StudentRoutes.RequirementsEPADetails}
                element={
                  <ProtectedRoute
                    element={<EPADetail />}
                    roles={[
                      ROLES.STUDENT,
                      ROLES.COACH,
                      ROLES.ADMIN,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                    ]}
                  />
                }
              />
              <Route
                path={StudentRoutes.RequirementsCompetencyDetails}
                element={
                  <ProtectedRoute
                    element={<CompetencyDetail />}
                    roles={[
                      ROLES.STUDENT,
                      ROLES.COACH,
                      ROLES.ADMIN,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                    ]}
                  />
                }
              />
              <Route
                path={StudentRoutes.RequirementsITERDetails}
                element={
                  <ProtectedRoute
                    element={<ITERDetail />}
                    roles={[
                      ROLES.STUDENT,
                      ROLES.COACH,
                      ROLES.ADMIN,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                    ]}
                  />
                }
              />

              <Route
                path={CoachRoutes.FeedbackPortal}
                element={
                  <ProtectedRoute
                    element={<FeedbackPortalList />}
                    roles={[
                      ROLES.COACH,
                      ROLES.STUDENT,
                      ROLES.ADMIN,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                    ]}
                  />
                }
              />
              <Route
                path={CoachRoutes.FeedbackDetail}
                element={
                  <ProtectedRoute
                    element={<FeedbackPortalDetail />}
                    roles={[
                      ROLES.COACH,
                      ROLES.STUDENT,
                      ROLES.ADMIN,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                    ]}
                  />
                }
              />

              <Route
                path={CoachRoutes.FeedbackGuide}
                element={
                  <ProtectedRoute
                    element={<FeedbackGuide />}
                    roles={[
                      ROLES.COACH,
                      ROLES.ADMIN,
                      ROLES.ADMIN,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                    ]}
                  />
                }
              />

              <Route
                path={StudentRoutes.RequestFeedback}
                element={
                  <ProtectedRoute
                    element={<RequestFeedback />}
                    roles={[ROLES.STUDENT, ROLES.ADMIN]}
                  />
                }
              />

              <Route
                path={CoachRoutes.RequestFeedback}
                element={
                  <ProtectedRoute
                    element={<RequestFeedback />}
                    roles={[ROLES.COACH, ROLES.ADMIN]}
                  />
                }
              />

              <Route
                path={CoachRoutes.GiveFeedback}
                element={
                  <ProtectedRoute
                    element={<RequestFeedback />}
                    roles={[ROLES.COACH, ROLES.ADMIN]}
                  />
                }
              />

              <Route
                path={CoachRoutes.GiveFeedbackForLearner}
                element={
                  <ProtectedRoute
                    element={<RequestFeedback />}
                    roles={[ROLES.COACH, ROLES.ADMIN]}
                  />
                }
              />

              <Route
                path={StudentRoutes.RequestFeedbackContinue}
                element={
                  <ProtectedRoute
                    element={<RequestFeedback />}
                    roles={[ROLES.STUDENT, ROLES.ADMIN]}
                  />
                }
              />

              <Route
                path={AdminRoutes.FeedbackList}
                element={
                  <ProtectedRoute
                    element={<FeedbackList />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />

              <Route
                path={AdminRoutes.FeedbackDetail}
                element={
                  <ProtectedRoute
                    element={<FeedbackPortalDetail />}
                    roles={[
                      ROLES.ADMIN,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                      ROLES.COACH,
                      ROLES.STUDENT,
                    ]}
                  />
                }
              />
              <Route
                path={AdminRoutes.FeedbackSubmissionConfirmation}
                element={
                  <ProtectedRoute
                    element={<FeedbackSubmissionConfirmation />}
                    roles={[
                      ROLES.ADMIN,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                      ROLES.COACH,
                      ROLES.STUDENT,
                    ]}
                  />
                }
              />

              <Route
                path={AdminRoutes.OrganizationFeedbackList}
                element={
                  <ProtectedRoute
                    element={<FeedbackList />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationAllFeedbackForUser}
                element={
                  <ProtectedRoute
                    element={<OrganizationUserFeedbackList />}
                    roles={[
                      ROLES.ADMIN,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                      ROLES.COACH,
                    ]}
                  />
                }
              />

              <Route
                path={AdminRoutes.OrganizationFeedbackDetail}
                element={
                  <ProtectedRoute
                    element={<FeedbackPortalDetail />}
                    roles={[
                      ROLES.ADMIN,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                      ROLES.COACH,
                      ROLES.STUDENT,
                    ]}
                  />
                }
              />

              <Route
                path={AdminRoutes.OrganizationYearlyCurriculum}
                element={
                  <ProtectedRoute
                    element={<YearlyCurriculumList />}
                    roles={[
                      ROLES.COACH,
                      ROLES.ADMIN,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                    ]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationYearlyCurriculumDetail}
                element={
                  <ProtectedRoute
                    element={<AcademicYearDetail />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationYearlyCurriculumTemplate}
                element={
                  <ProtectedRoute
                    element={<AcademicYearDetail />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationYearlyCurriculumCreate}
                element={
                  <ProtectedRoute
                    element={<AcademicYearCreateByOrgProgram />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationYearlyCurriculumEdit}
                element={
                  <ProtectedRoute
                    element={<AcademicCreateOrEdit program edit />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationAnalytics}
                element={
                  <ProtectedRoute
                    element={<Analytics />}
                    roles={[ROLES.ADMIN, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.Metrics}
                element={
                  <ProtectedRoute
                    element={<Metrics />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.HospitalCompetition}
                element={
                  <ProtectedRoute
                    element={<HospitalCompetition />}
                    roles={[
                      ROLES.ADMIN,
                      ROLES.COACH,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                      ROLES.STUDENT,
                    ]}
                  />
                }
              />
              <Route
                path={AdminRoutes.EducationRecordAccessLog}
                element={
                  <ProtectedRoute
                    element={<EducationRecordAccessLog />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path="/organization/:organizationId/metrics"
                element={<Navigate to={AdminRoutes.Metrics} replace />}
              />

              <Route
                path={AdminRoutes.OrganizationRequirementsEPAList}
                element={
                  <ProtectedRoute
                    element={<EPAList />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationDomainsList}
                element={
                  <ProtectedRoute
                    element={<DomainList />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationUploadCSVRequirementsEPA}
                element={
                  <ProtectedRoute
                    element={
                      <UploadCSVRequirements mode="org" requirementType="EPA" />
                    }
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationUploadCSVRequirementsCompetency}
                element={
                  <ProtectedRoute
                    element={
                      <UploadCSVRequirements
                        mode="org"
                        requirementType="COMPETENCY"
                      />
                    }
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationUploadCSVRequirementsITER}
                element={
                  <ProtectedRoute
                    element={
                      <UploadCSVRequirements
                        mode="org"
                        requirementType="ITER"
                      />
                    }
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationUploadSpreadsheetFramework}
                element={
                  <ProtectedRoute
                    element={<ImportFrameworkSpreadsheet mode="org" />}
                    roles={[ROLES.ADMIN]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationRequirementsEPACreate}
                element={
                  <ProtectedRoute
                    element={<EPACreateAndEdit />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationRequirementsEPAView}
                element={
                  <ProtectedRoute
                    element={<EPADetail />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationRequirementsEPAEdit}
                element={
                  <ProtectedRoute
                    element={<EPACreateAndEdit edit />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationRequirementsCompetencyList}
                element={
                  <ProtectedRoute
                    element={<CompetencyList />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationRequirementsCompetencyCreate}
                element={
                  <ProtectedRoute
                    element={<CompetencyCreateAndEdit />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationRequirementsCompetencyView}
                element={
                  <ProtectedRoute
                    element={<CompetencyDetail />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationRequirementsCompetencyEdit}
                element={
                  <ProtectedRoute
                    element={<CompetencyCreateAndEdit edit />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationRequirementsITERList}
                element={
                  <ProtectedRoute
                    element={<ITERList />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationRequirementsITERCreate}
                element={
                  <ProtectedRoute
                    element={<ITERCreateAndEdit />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationRequirementsITERView}
                element={
                  <ProtectedRoute
                    element={<ITERDetail />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationRequirementsITEREdit}
                element={
                  <ProtectedRoute
                    element={<ITERCreateAndEdit edit />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationSurveysList}
                element={
                  <ProtectedRoute
                    element={<SurveyList />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationSurveyCreate}
                element={
                  <ProtectedRoute
                    element={<SurveyCreateAndEdit />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationSurveyEdit}
                element={
                  <ProtectedRoute
                    element={<SurveyCreateAndEdit edit />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path="/organization/:organizationId/survey/:surveyId"
                element={
                  <ProtectedRoute
                    element={<SurveyDetail />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationSurveyAnswer}
                element={
                  <ProtectedRoute
                    element={<SurveyAnswer />}
                    roles={[
                      ROLES.ADMIN,
                      ROLES.STUDENT,
                      ROLES.COACH,
                      ROLES.DIRECTOR,
                      ROLES.MANAGER,
                    ]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationAnswersList}
                element={
                  <ProtectedRoute
                    element={<AnswersList />}
                    roles={[ROLES.ADMIN, ROLES.DIRECTOR, ROLES.MANAGER]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationSurveyAnswers}
                element={
                  <ProtectedRoute
                    element={<SurveyAnswers />}
                    roles={[ROLES.ADMIN, ROLES.DIRECTOR, ROLES.MANAGER]}
                  />
                }
              />
              <Route
                path={AdminRoutes.OrganizationAnswerDetail}
                element={
                  <ProtectedRoute
                    element={<AnswerDetail />}
                    roles={[ROLES.ADMIN, ROLES.DIRECTOR, ROLES.MANAGER]}
                  />
                }
              />

              <Route
                path={AdminRoutes.GraduationClassList}
                element={
                  <ProtectedRoute
                    element={<GraduationClassList />}
                    roles={[
                      ROLES.COACH,
                      ROLES.ADMIN,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                    ]}
                  />
                }
              />
              <Route
                path={AdminRoutes.GraduationClassCreate}
                element={
                  <ProtectedRoute
                    element={<GraduationClassCreateOrEdit />}
                    roles={[
                      ROLES.COACH,
                      ROLES.ADMIN,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                    ]}
                  />
                }
              />
              <Route
                path={AdminRoutes.GraduationClassEdit}
                element={
                  <ProtectedRoute
                    element={<GraduationClassCreateOrEdit edit />}
                    roles={[
                      ROLES.COACH,
                      ROLES.ADMIN,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                    ]}
                  />
                }
              />
              <Route
                path={AdminRoutes.GraduationClassUploadUsers}
                element={
                  <ProtectedRoute
                    element={<UploadCSVUsers roles={[ROLES.STUDENT]} />}
                    roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.DIRECTOR]}
                  />
                }
              />

              <Route
                path={AdminRoutes.GraduationClassView}
                element={
                  <ProtectedRoute
                    element={<GraduationClassDetail />}
                    roles={[
                      ROLES.COACH,
                      ROLES.ADMIN,
                      ROLES.MANAGER,
                      ROLES.DIRECTOR,
                    ]}
                  />
                }
              />

              <Route
                path={StudentRoutes.FeedbackPortal}
                element={
                  <ProtectedRoute
                    element={<UnderConstruction />}
                    roles={[ROLES.STUDENT]}
                  />
                }
              />
              <Route
                path={StudentRoutes.Calendar}
                element={
                  <ProtectedRoute
                    element={<UnderConstruction />}
                    roles={[ROLES.STUDENT]}
                  />
                }
              />
              <Route
                path={StudentRoutes.Survey}
                element={
                  <ProtectedRoute
                    element={<UnderConstruction />}
                    roles={[ROLES.STUDENT]}
                  />
                }
              />
            </Route>

            <Route element={<ProtectedLayout headerOnly={true} />}>
              <Route path="/nodata" element={<NoData />} />
              <Route path="*" element={<NoMatch />} />
              <Route path="/403" element={<Unauthorized />} />
            </Route>
            <Route
              path={AdminRoutes.ResetPassword}
              element={<ResetPassword />}
            />
            <Route path="/terms" element={<TermsOfUse />} />
          </Routes>
          <Notification onClose={() => {}} />
        </Router>
      </ApolloProvider>
    </ErrorBoundary>
  );
};

export default App;
